// utils for working with localization / i18n machinery
// this is used by our other locale/i18n stuff -- this should not import from those places


/**
 * 
 * @param {string|Intl.Locale} locale - the locale code (e.g., 'en', 'es', 'es-MX', 'fr') or an Intl.Locale object
 * @returns {Intl.Locale|null} - returns null only if the input is null or undefined; otherwise returns an Intl.Locale object
 * 
 * Usage:
 * import { getLocaleObject } from '@/localization/utils';
 * 
 * const localeObj = getLocaleObject(this.$splLocale);
 */
const getLocaleObject = (locale) => {
    // return an instance of `Intl.Locale` for the given locale string
    if (!locale) return null;
    if (locale instanceof Intl.Locale) {
        return locale;  // already an Intl.Locale object
    }
    // we ensure we have dashes, not _underscores which are common in our backend -- those throw Errors here
    // (but don't get in the habit of using '_' in the frontend, just use '-' for region codes)
    const dashed = locale.replace(/_/g, '-');  // replace '_' with '-' 
    const localeObj = new Intl.Locale(dashed);  // let it throw if invalid
    return localeObj;
}


/**
 * 
 * @param {string|Intl.Locale} locale - the locale code (e.g., 'en', 'es', 'es-MX', 'fr') or an Intl.Locale object
 * @returns {string} - the normalized locale string (e.g., 'en', 'es', 'es-MX', 'fr') -- only includes the region if available
 */
const normalizeLocale = (locale) => {
    // for frontend, we will always normalize to the 'canonical' form of locale, as defined in standard Intl.Locale API
    if (!locale) return 'en';
    const localeObj = getLocaleObject(locale);
    return localeObj.baseName;
};

/**
 * 
 * @param {string|Intl.Locale} locale - the locale code (e.g., 'en', 'es', 'es-MX', 'fr') or an Intl.Locale object
 * @returns {string[]} - an array of possible locale strings, in order of preference
 */
const enumeratePossibleLocales = (locale) => {
    // return an array of possible locales to try, in order of preference
    // this does NOT add a default, unless you pass in something completely blank
    // e.g., if locale is 'es-MX', return ['es-MX', 'es']
    // e.g., if locale is 'es', return ['es']
    // e.g., if locale is 'en-US', return ['en-US', 'en']
    // e.g., if locale is 'en', return ['en']
    if (!locale) return ['en'];
    const localeObj = getLocaleObject(locale);
    const possibleLocales = [localeObj.baseName];  // always start with that
    if (localeObj.region && localeObj.script && localeObj.language) {
        // if lang + script + region are present, we will push BOTH the lang-script and lang-region versions, then the lang-only version
        // we'll also push the lang-script-region version if it is not equal to the baseName (which is the canonical form of the locale) -- possible with some corner cases with extra stuff after the region
        const langScriptRegion = `${localeObj.language}-${localeObj.script}-${localeObj.region}`;
        if (langScriptRegion !== localeObj.baseName) {
            possibleLocales.push(langScriptRegion);  // e.g., 'zh-Hans-CN'
        }
        possibleLocales.push(`${localeObj.language}-${localeObj.script}`);  // e.g., 'zh-Hans'  -- should this be before the lang-region version?  
        possibleLocales.push(`${localeObj.language}-${localeObj.region}`);  // e.g., 'zh-CN'
        possibleLocales.push(`${localeObj.language}`);  // e.g., 'zh'
    } else if (localeObj.region && localeObj.language) {
        // if only lang + region is present, we will push the lang-region version, then the lang-only version
        const langRegion = `${localeObj.language}-${localeObj.region}`;
        if (langRegion !== localeObj.baseName) {
            possibleLocales.push(langRegion);  // e.g., 'es-MX'
        }
        possibleLocales.push(`${localeObj.language}`);  // e.g., 'es'
    } else if (localeObj.language) {
        // if only lang is present, we will push the lang-only version
        possibleLocales.push(`${localeObj.language}`);  // e.g., 'en'
    }
    return possibleLocales;
}


export { 
    normalizeLocale,
    getLocaleObject,
    enumeratePossibleLocales,
};